import { z } from 'zod';
/** Banco por el que se paga: BAC o BN. */
export const BancoPagoSchema = z.enum(['BAC', 'BN']);
/** Lo que se guarda en un pago: un banco o 'MANUAL' (se paga a mano, sin archivo). */
export const BancoAsignadoSchema = z.enum(['BAC', 'BN', 'MANUAL']);
export const MonedaSchema = z.enum(['C', 'D']);
export const TipoConceptoSchema = z.enum(['recurrente', 'esporadico']);
export const PeriodicidadSchema = z.enum(['mensual', 'bimestral', 'trimestral', 'semestral']);
/**
 * Una cuenta de pago de un proveedor. El IBAN se normaliza en el servidor (sin espacios ni guiones,
 * en mayúscula). Con `id` se actualiza esa cuenta; sin `id` se crea.
 */
export const CuentaProveedorSchema = z.object({
    id: z.number().int().positive().optional(),
    moneda: MonedaSchema,
    iban: z.string().min(1).max(40),
    banco: z.string().max(100).nullable().optional(),
    tipoCuenta: z.string().max(50).nullable().optional(),
    predeterminada: z.boolean().optional(),
});
export const CrearProveedorSchema = z.object({
    nombre: z.string().min(1).max(250),
    cedula: z.string().max(20).nullable().optional(),
    cuentas: z.array(CuentaProveedorSchema).max(20).optional(),
    notas: z.string().max(500).nullable().optional(),
    vendorCode: z.string().max(20).nullable().optional(),
    esEmpresaRelacionada: z.boolean().optional(),
});
export const ActualizarProveedorSchema = CrearProveedorSchema.partial().extend({
    activo: z.boolean().optional(),
    /**
     * Agrega una cuenta solo si ese IBAN todavía no está en el proveedor (la usa la importación de
     * referencias de BAC, que no debe pisar las cuentas que ya tiene).
     */
    agregarCuenta: CuentaProveedorSchema.optional(),
});
export const CrearConceptoSchema = z.object({
    nombre: z.string().min(1).max(250),
    moneda: MonedaSchema,
    tipo: TipoConceptoSchema.optional(),
    categoriaId: z.number().int().positive(),
    proveedorId: z.number().int().positive().nullable().optional(),
    orden: z.number().int().min(0).optional(),
    notas: z.string().max(500).nullable().optional(),
    tieneObligaciones: z.boolean().optional(),
    periodicidad: PeriodicidadSchema.nullable().optional(),
    montoCuotaFijo: z.number().nonnegative().nullable().optional(),
    bancoPorDefecto: BancoPagoSchema.nullable().optional(),
});
export const ActualizarConceptoSchema = CrearConceptoSchema.partial().extend({
    activo: z.boolean().optional(),
});
/** Confirmar la sugerencia de vínculo de un concepto con un proveedor de Business Central. */
export const VincularProveedorConceptoSchema = z.object({
    vendorCode: z.string().min(1).max(20),
    vendorName: z.string().min(1).max(250),
});
export const CrearSemanaSchema = z.object({
    numeroSemana: z.number().int().min(1).max(53),
    anio: z.number().int().min(2020).max(2100),
    fechaInicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    fechaFin: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    tipoDeCambio: z.number().positive().nullable().optional(),
    bancoColones: z.number().nullable().optional(),
    bancoDolares: z.number().nullable().optional(),
    copiarPendientesDeId: z.number().int().positive().nullable().optional(),
});
export const ActualizarSaldosSchema = z.object({
    tipoDeCambio: z.number().positive().nullable().optional(),
    quinta: z.number().nullable().optional(),
    bancoColones: z.number().nullable().optional(),
    bancoDolares: z.number().nullable().optional(),
});
export const ActualizarPagoSchema = z.object({
    monedaPago: MonedaSchema.nullable().optional(),
    montoAdeudado: z.number().nullable().optional(),
    montoProgramado: z.number().nullable().optional(),
    montoPagado: z.number().nullable().optional(),
    pagado: z.boolean().optional(),
    notas: z.string().max(500).nullable().optional(),
    /** Banco de este pago. null = vuelve a usar el banco por defecto del concepto. */
    banco: BancoAsignadoSchema.nullable().optional(),
    /** Cuenta del proveedor elegida a mano. null = automática (la del mismo banco que el de salida). */
    proveedorCuentaId: z.number().int().positive().nullable().optional(),
});
export const CrearObligacionSchema = z.object({
    conceptoId: z.number().int().positive(),
    periodoDescripcion: z.string().min(1).max(100),
    periodoFecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    monto: z.number().nonnegative().nullable().optional(),
    notas: z.string().max(500).nullable().optional(),
});
export const ActualizarObligacionSchema = z.object({
    monto: z.number().nonnegative().nullable().optional(),
    pagado: z.boolean().optional(),
    semanaPagoId: z.number().int().positive().nullable().optional(),
    notas: z.string().max(500).nullable().optional(),
});
export const CrearPagoAdicionalSchema = z.object({
    categoriaId: z.number().int().positive(),
    nombre: z.string().min(1).max(250),
    moneda: MonedaSchema,
    montoAdeudado: z.number().nullable().optional(),
    montoProgramado: z.number().nullable().optional(),
    proveedorId: z.number().int().positive().nullable().optional(),
});
export const CrearPagoSchema = z.object({
    semanaId: z.number().int().positive(),
    conceptoId: z.number().int().positive(),
    montoProgramado: z.number().nullable().optional(),
    montoPagado: z.number().nullable().optional(),
    pagado: z.boolean().optional(),
    notas: z.string().max(500).nullable().optional(),
    /** Banco de este pago. null = vuelve a usar el banco por defecto del concepto. */
    banco: BancoAsignadoSchema.nullable().optional(),
    /** Cuenta del proveedor elegida a mano. null = automática (la del mismo banco que el de salida). */
    proveedorCuentaId: z.number().int().positive().nullable().optional(),
});
/**
 * Plan de pagos de un caso (Bonificaciones): un pago único o una cuota mensual repetida. El total del caso
 * es monto de la cuota x número de cuotas; la fecha es la de la primera cuota.
 */
export const PlanCasoSchema = z.object({
    periodicidad: z.enum(['UNICA', 'MENSUAL']),
    montoCuota: z.number().positive(),
    numCuotas: z.number().int().min(1).max(240),
    fechaInicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida'),
});
/** Datos de un caso de detalle (ej. una liquidación laboral): a quién se le debe y cuánto en total. */
export const ActualizarDetalleCasoSchema = z.object({
    beneficiario: z.string().trim().min(1).max(200),
    descripcion: z.string().trim().max(300).nullable().optional(),
    moneda: MonedaSchema,
    montoTotal: z.number().positive(),
    /** Proveedor o beneficiario (pg.proveedores) a quien se le paga; obligatorio según la categoría. */
    proveedorId: z.number().int().positive().nullable().optional(),
    /** Beneficiario (pg.beneficiarios): colaborador o cliente; en lugar del proveedor según la categoría. */
    beneficiarioId: z.number().int().positive().nullable().optional(),
    /** Plan de cuotas; solo lo usan las categorías con plan. Con plan, el total se calcula en el servidor. */
    plan: PlanCasoSchema.nullable().optional(),
});
/** La semana de inicio es la primera en la que el caso aparece en Pagos Semanales. */
export const CrearDetalleCasoSchema = ActualizarDetalleCasoSchema.extend({
    semanaId: z.number().int().positive(),
    categoriaId: z.number().int().positive(),
});
/** Lo que se paga de un caso en una semana; pagado = confirmado, y su monto sale del saldo. */
export const PagoDetalleCasoSchema = z.object({
    semanaId: z.number().int().positive(),
    montoAPagar: z.number().min(0),
    pagado: z.boolean(),
    /** Banco de este pago; si no se manda se conserva el que tenía (o se sugiere por la cuenta del proveedor). */
    banco: BancoAsignadoSchema.nullable().optional(),
    /** Cuenta del proveedor elegida a mano; null = automática. */
    proveedorCuentaId: z.number().int().positive().nullable().optional(),
});
/** Eliminar conceptos: `soloVerificar` solo cuenta su historial; sin `borrarHistorial` los que tienen historial se archivan. */
export const EliminarConceptosSchema = z.object({
    ids: z.array(z.number().int().positive()).min(1).max(500),
    borrarHistorial: z.boolean().default(false),
    soloVerificar: z.boolean().default(false),
});
/** Beneficiario: un colaborador se crea por su id en dbo.Colaborador; un cliente u otro con nombre y cédula. */
export const CrearBeneficiarioSchema = z.object({
    tipo: z.enum(['COLABORADOR', 'CLIENTE', 'OTRO']),
    colaboradorId: z.number().int().positive().optional(),
    nombre: z.string().trim().max(200).optional(),
    cedula: z.string().trim().max(30).optional(),
});
export const CrearCuentaBeneficiarioSchema = z.object({
    moneda: MonedaSchema,
    iban: z.string().trim().min(15).max(40),
});
//# sourceMappingURL=flujo.schema.js.map